import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { GOOGLE_MAPS_API_KEY, PLACES_COUNTRY, PLACES_DEBOUNCE_MS, PLACES_LANGUAGE, PLACES_MAX_RESULTS } from '../constants';

interface LocationValue {
  address: string;
  coordinates: { latitude: number; longitude: number };
}

interface Props {
  value: LocationValue | null;
  onChange: (value: LocationValue | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
}

// Buiten de component: een nieuwe array-referentie laat useJsApiLoader opnieuw laden.
const LIBRARIES: 'places'[] = ['places'];

/**
 * Adresveld dat exact dezelfde suggesties en hetzelfde adres oplevert als de app
 * (`app/components/LocationInput.js`).
 *
 * De app roept de Places REST-API aan met `language=nl` en `components=country:nl`,
 * en haalt het gekozen adres op via **Place Details** (`formatted_address` +
 * `geometry.location`). Die REST-endpoints sturen geen CORS-headers, dus vanuit de
 * browser gaat het via de JavaScript-SDK — dezelfde backend, dezelfde parameters,
 * dus dezelfde respons.
 *
 * Wat hier eerder misging:
 *  - geen `language`/`components`, dus andere (en niet-Nederlandse) suggesties;
 *  - `geocodeByPlaceId()` gebruikte de **Geocoding**-API in plaats van Place Details,
 *    wat voor hetzelfde punt een ander `formatted_address` kan teruggeven;
 *  - de API werd twee keer ingeladen (`useJsApiLoader` én de `apiKey`-prop van
 *    react-google-places-autocomplete), waardoor de suggesties nooit terugkwamen;
 *  - fouten van Google werden nergens getoond — het veld bleef eeuwig op "Loading…".
 */
const GooglePlacesAutocompleteInput: React.FC<Props> = ({ value, onChange, label, placeholder, error }) => {
  const { t } = useTranslation();

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'homeme-google-maps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    language: PLACES_LANGUAGE,
    region: PLACES_COUNTRY.toUpperCase(),
  });

  const [term, setTerm] = useState(value?.address || '');
  const [results, setResults] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [apiError, setApiError] = useState<string | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const detailsRef = useRef<google.maps.places.PlacesService | null>(null);
  const debounceRef = useRef<number | undefined>(undefined);
  const sessionRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  // Volg de waarde van buitenaf (bijv. bij het openen van een bestaande woning).
  useEffect(() => { setTerm(value?.address || ''); }, [value?.address]);

  // Een geweigerde sleutel meldt zich via deze globale callback; zonder dit bleef
  // het veld stil hangen. De melding noemt de URL die in Google Cloud moet staan.
  useEffect(() => {
    (window as any).gm_authFailure = () => {
      setApiError(t('places_auth_error', 'Google weigert de API-sleutel voor {{origin}}. Voeg deze URL toe bij de sleutelbeperkingen in Google Cloud.', { origin: window.location.origin }));
      setSearching(false);
    };
  }, [t]);

  useEffect(() => {
    if (!isLoaded || serviceRef.current) return;
    serviceRef.current = new google.maps.places.AutocompleteService();
    // PlacesService heeft een DOM-node of kaart nodig; een losse div volstaat.
    detailsRef.current = new google.maps.places.PlacesService(document.createElement('div'));
    sessionRef.current = new google.maps.places.AutocompleteSessionToken();
  }, [isLoaded]);

  // Klik buiten het veld sluit de lijst.
  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => () => window.clearTimeout(debounceRef.current), []);

  const search = useCallback((input: string) => {
    const service = serviceRef.current;
    if (!service || input.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    service.getPlacePredictions(
      {
        input,
        // Gelijk aan de app: language=nl, components=country:nl
        componentRestrictions: { country: PLACES_COUNTRY },
        language: PLACES_LANGUAGE,
        sessionToken: sessionRef.current || undefined,
      },
      (predictions, status) => {
        setSearching(false);
        const OK = google.maps.places.PlacesServiceStatus;
        if (status === OK.OK && predictions) {
          setResults(predictions.slice(0, PLACES_MAX_RESULTS));
          setApiError(null);
        } else if (status === OK.ZERO_RESULTS) {
          setResults([]);
          setApiError(null);
        } else {
          setResults([]);
          setApiError(t('places_error', 'Adressen ophalen lukt niet ({{status}}).', { status }));
        }
      },
    );
  }, [t]);

  const handleInput = (next: string) => {
    setTerm(next);
    setOpen(true);
    setActiveIndex(-1);
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => search(next), PLACES_DEBOUNCE_MS);
  };

  const pick = (prediction: google.maps.places.AutocompletePrediction) => {
    const service = detailsRef.current;
    if (!service) return;
    window.clearTimeout(debounceRef.current);
    setOpen(false);
    setResults([]);

    // Place Details — net als de app. Niet de Geocoding-API, die een ander
    // formatted_address kan teruggeven voor hetzelfde place_id.
    service.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['formatted_address', 'geometry'],
        language: PLACES_LANGUAGE,
        sessionToken: sessionRef.current || undefined,
      },
      (place, status) => {
        // Elke sessie eindigt bij een details-aanvraag; daarna een verse token.
        sessionRef.current = new google.maps.places.AutocompleteSessionToken();

        if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) {
          setApiError(t('places_details_error', 'Dit adres kon niet worden opgehaald ({{status}}).', { status }));
          return;
        }
        // Zelfde terugval als de app wanneer formatted_address ontbreekt.
        const { main_text: main, secondary_text: secondary } = prediction.structured_formatting;
        const address = place.formatted_address || (secondary ? `${main}, ${secondary}` : main);

        setApiError(null);
        setTerm(address);
        onChange({
          address,
          coordinates: {
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
          },
        });
      },
    );
  };

  const clear = () => {
    window.clearTimeout(debounceRef.current);
    setTerm('');
    setResults([]);
    setOpen(false);
    setApiError(null);
    onChange(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => (i >= results.length - 1 ? 0 : i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => (i <= 0 ? results.length - 1 : i - 1)); }
    else if (e.key === 'Enter' && activeIndex >= 0) { e.preventDefault(); pick(results[activeIndex]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const message = useMemo(() => {
    if (loadError) return t('places_load_error', 'Google Maps kon niet worden geladen.');
    return apiError || error || null;
  }, [loadError, apiError, error, t]);

  return (
    <div className="w-full">
      {label && <label htmlFor="listing-address" className="mb-1 block text-sm font-medium text-neutral-700">{label}</label>}

      <div className="relative" ref={wrapperRef}>
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-homeme">
          <svg className="h-4.5 w-4.5" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" />
            <circle cx="12" cy="10" r="2.6" />
          </svg>
        </span>

        <input
          id="listing-address"
          type="text"
          value={term}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          onKeyDown={handleKeyDown}
          disabled={!isLoaded && !loadError}
          placeholder={isLoaded ? (placeholder || t('address_placeholder', 'Zoek een adres')) : t('loading', 'Laden...')}
          autoComplete="off"
          role="combobox"
          aria-expanded={open && results.length > 0}
          aria-autocomplete="list"
          aria-invalid={Boolean(message)}
          className={`w-full rounded-lg border py-2 pl-10 pr-9 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-homeme/25
            ${message ? 'border-red-400' : 'border-neutral-300 focus:border-homeme'}
            disabled:bg-neutral-100 disabled:text-neutral-400`}
        />

        <span className="absolute right-3 top-1/2 -translate-y-1/2">
          {searching ? (
            <svg className="h-4 w-4 animate-spin text-neutral-400" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
              <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
          ) : term ? (
            <button type="button" onClick={clear} aria-label={t('clear', 'Wissen')} className="text-neutral-400 transition-colors hover:text-neutral-700 focus:outline-none">
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <path d="M5 5l10 10M15 5L5 15" />
              </svg>
            </button>
          ) : null}
        </span>

        {open && results.length > 0 && (
          <ul role="listbox" className="absolute z-30 mt-1.5 w-full overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg">
            {results.map((prediction, index) => (
              <li
                key={prediction.place_id}
                role="option"
                aria-selected={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={e => e.preventDefault()}
                onClick={() => pick(prediction)}
                className={`cursor-pointer border-b border-neutral-100 px-3.5 py-2 last:border-b-0 ${index === activeIndex ? 'bg-neutral-50' : ''}`}
              >
                <span className="block text-sm font-semibold text-neutral-800">{prediction.structured_formatting.main_text}</span>
                {prediction.structured_formatting.secondary_text && (
                  <span className="block text-xs text-neutral-500">{prediction.structured_formatting.secondary_text}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {message && <p className="mt-1 text-xs text-red-600">{message}</p>}
    </div>
  );
};

export default GooglePlacesAutocompleteInput;
